import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { RecipeForm } from '../components/recipe/RecipeForm';
import { getRecipe, updateRecipe } from '../services/recipe';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorBoundary } from '../components/ui/ErrorBoundary';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { RecipeInput } from '../schemas/recipe';

export default function EditRecipePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: recipe, isLoading: isLoadingRecipe } = useQuery({
    queryKey: ['recipe', id],
    queryFn: () => getRecipe(id!),
    enabled: !!id,
    staleTime: Infinity, // Prevent unnecessary refetches while editing
  });
  
  const mutation = useMutation({
    mutationFn: async (data: RecipeInput) => {
      console.log('Updating recipe with data:', data);
      return updateRecipe(id!, data);
    },
    onMutate: async (newData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['recipe', id] });
      await queryClient.cancelQueries({ queryKey: ['recipes'] });

      // Snapshot the previous values
      const previousRecipe = queryClient.getQueryData(['recipe', id]);
      const previousRecipes = queryClient.getQueryData(['recipes']);

      // Optimistically update the cache
      queryClient.setQueryData(['recipe', id], old => ({
        ...old,
        ...newData,
        updatedAt: new Date()
      }));

      // Return context with the snapshotted values
      return { previousRecipe, previousRecipes };
    },
    onSuccess: async (updatedRecipe) => {
      // Update both caches with the new data
      queryClient.setQueryData(['recipe', id], updatedRecipe);
      queryClient.setQueryData(['recipes'], (old: any[]) => 
        old?.map(recipe => recipe.id === id ? updatedRecipe : recipe)
      );

      toast.success('Recipe updated successfully!');
      navigate(`/recipes/${id}`);
    },
    onError: (error, newData, context) => {
      console.error('Failed to update recipe:', error);
      // Rollback to the previous state if available
      if (context?.previousRecipe) {
        queryClient.setQueryData(['recipe', id], context.previousRecipe);
      }
      if (context?.previousRecipes) {
        queryClient.setQueryData(['recipes'], context.previousRecipes);
      }
      toast.error(error instanceof Error ? error.message : 'Failed to update recipe. Please try again.');
    },
    onSettled: () => {
      // Refetch after error or success to ensure cache consistency
      queryClient.invalidateQueries({ queryKey: ['recipe', id] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    }
  });

  if (isLoadingRecipe) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-900">Recipe not found</h2>
      </div>
    );
  }

  // Transform the recipe data to match the form input structure
  const formData: RecipeInput = {
    title: recipe.title,
    category: recipe.category,
    ingredients: recipe.ingredients,
    instructions: recipe.instructions,
    cookingTime: recipe.cookingTime,
    prepTime: recipe.prepTime,
    servings: recipe.servings,
    difficulty: recipe.difficulty,
    dietaryTags: recipe.dietaryTags,
  };

  return (
    <ErrorBoundary
      fallback={
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Something went wrong</h2>
          <p className="text-gray-600 mb-4">There was an error updating the recipe.</p>
          <button
            onClick={() => window.location.reload()}
            className="text-indigo-600 hover:text-indigo-500"
          >
            Try again
          </button>
        </div>
      }
    >
      <div className="max-w-3xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Edit Recipe</h1>
        <RecipeForm 
          initialData={formData}
          onSubmit={(data) => {
            console.log('Form submitted with data:', data);
            mutation.mutate(data);
          }} 
          isLoading={mutation.isPending}
          disabled={mutation.isPending} 
        />
      </div>
    </ErrorBoundary>
  );
}